import React, { useEffect, useRef, useState } from 'react';
import { searchFirmMembers } from '../../lib/db';

/**
 * Lightweight @-mention picker for textarea inputs.
 *
 * Usage:
 *   const picker = useMentionPicker(textareaRef, {
 *     value, setValue,
 *     getCurrentUserId: () => userProfile?.id,
 *   });
 *   // ...
 *   <textarea ref={textareaRef} value={value} ... />
 *   {picker.element}
 *   // On submit:
 *   const mentions = picker.consumeMentions(); // returns uuid[] for storage
 *
 * Inline format we insert into the textarea: `@[First Last](uuid)` — this
 * matches what `MessageBody` renders so the on-screen display can show the
 * name while we keep the resolved uuid for the `mentions` column.
 */

function useDebounced(value, delay = 150) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// Finds the active @-token immediately to the left of the caret. Returns
// { token, start, end } where token is the text after @ and (start, end)
// is the substring to replace when a mention is chosen.
function findActiveMention(text, caret) {
  if (caret == null) return null;
  const upTo = text.slice(0, caret);
  const at   = upTo.lastIndexOf('@');
  if (at < 0) return null;
  // Must be at start of string or preceded by whitespace / bracket.
  if (at > 0 && !/[\s([]/.test(upTo[at - 1])) return null;
  const query = upTo.slice(at + 1);
  // @mentions don't span whitespace; if the user typed a space, give up.
  if (/\s/.test(query)) return null;
  // Cap query length to avoid scanning huge buffers.
  if (query.length > 40) return null;
  return { token: query, start: at, end: caret };
}

function fullName(u) {
  return [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim() || u?.email || 'Member';
}

export function useMentionPicker(textareaRef, { value, setValue }) {
  const [active, setActive]     = useState(null);   // { token, start, end }
  const [results, setResults]   = useState([]);
  const [highlight, setHighlight] = useState(0);
  // Track the resolved mentions so we can return only those that are still
  // present in the body when the message is submitted.
  const resolvedRef = useRef(new Map()); // uuid → name
  const debouncedToken = useDebounced(active?.token ?? null, 120);

  // Detect active mention from the textarea state.
  const onSelectionChange = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const caret = el.selectionStart;
    const next  = findActiveMention(el.value, caret);
    setActive(next);
    setHighlight(0);
  }, [textareaRef]);

  // Re-evaluate active mention whenever the text changes.
  useEffect(() => { onSelectionChange(); }, [value, onSelectionChange]);

  // Fetch suggestions when the active token changes.
  useEffect(() => {
    if (debouncedToken == null) { setResults([]); return; }
    let cancelled = false;
    searchFirmMembers(debouncedToken).then(({ data }) => {
      if (cancelled) return;
      setResults(Array.isArray(data) ? data : []);
    });
    return () => { cancelled = true; };
  }, [debouncedToken]);

  const insertMention = (member) => {
    if (!active) return;
    const display = fullName(member);
    const token   = `@[${display}](${member.id})`;
    const next = value.slice(0, active.start) + token + ' ' + value.slice(active.end);
    setValue(next);
    resolvedRef.current.set(member.id, display);
    setActive(null);
    setResults([]);
    // Restore caret after the inserted token + space.
    queueMicrotask(() => {
      const el = textareaRef.current;
      if (!el) return;
      const pos = active.start + token.length + 1;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  // Keyboard handler the parent should forward from onKeyDown.
  const onKeyDown = (e) => {
    if (!active || results.length === 0) return false;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); return true; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); return true; }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(results[highlight]);
      return true;
    }
    if (e.key === 'Escape') { setActive(null); setResults([]); return true; }
    return false;
  };

  // Return the uuid[] of mentions still present in the body when submitting.
  const consumeMentions = () => {
    const re = /@\[[^\]]+\]\(([0-9a-f-]{36})\)/gi;
    const ids = new Set();
    let m;
    while ((m = re.exec(value))) ids.add(m[1]);
    return Array.from(ids);
  };

  // Render the suggestion list as a floating panel anchored to the
  // textarea. We don't bother with sophisticated positioning — anchored
  // top-left of the textarea works for a small picker.
  const showing = active && results.length > 0;
  const element = !showing ? null : (
    <div className="absolute z-30 mt-1 max-h-56 w-64 overflow-y-auto rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg">
      {results.map((m, i) => (
        <button
          key={m.id}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
          className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition ${
            i === highlight
              ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200'
              : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          <span className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[11px] font-medium text-slate-600 dark:text-slate-300 flex-shrink-0">
            {(m.first_name || m.email || '?').charAt(0).toUpperCase()}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block truncate">{fullName(m)}</span>
            <span className="block text-[11px] text-slate-500 truncate">{m.email}</span>
          </span>
        </button>
      ))}
    </div>
  );

  return { onKeyDown, onSelectionChange, consumeMentions, element };
}

/**
 * Renders a message body with @[Name](uuid) tokens replaced by a styled
 * pill. Used in both the firm and (eventually) portal renderers.
 */
export function MessageBody({ body, currentUserId }) {
  if (!body) return null;
  const out = [];
  const re = /@\[([^\]]+)\]\(([0-9a-f-]{36})\)/gi;
  let last = 0;
  let m;
  while ((m = re.exec(body))) {
    if (m.index > last) out.push(body.slice(last, m.index));
    const isMe = m[2] === currentUserId;
    out.push(
      <span
        key={`${m.index}-${m[2]}`}
        className={`px-1 rounded font-medium ${
          isMe
            ? 'bg-amber-200 dark:bg-amber-500/30 text-amber-900 dark:text-amber-100'
            : 'bg-indigo-100 dark:bg-indigo-500/30 text-indigo-700 dark:text-indigo-200'
        }`}
      >
        @{m[1]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push(body.slice(last));
  return <>{out}</>;
}
